import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../Helpers/env";
import { LoadingGif } from "../Utils/Loader";
import MainSnackbar from "../Utils/Snackbar/SmsSnackbar";
import { Container } from "../Utils/styledComponents";
import PageTitle from "../Utils/smsTitle";
import { SubHeader } from "../../Containers/SubHeader";

// FIXED IMPORTS — THIS IS THE KEY
import { faFileExcel, faFilePdf, faEye, faPills } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../actions/snackbarActions";

// PERFECT LINK LOGIC — 100% ACCURATE
const getVisitLink = (row) => {
  // visit_type is now STRING from backend → compare as string
  if (row.payment_type === "INSURANCE") {
    return `/invoice-details/${row.visit_id}/${row.provider_id || 0}/${row.claim_status || 0}/${row.provider_type || 'private'}`;
  }
  if (row.visit_type === '1') {
    return `/billingreceipt/${row.visit_id}`;
  }
  if (row.visit_type === '2') {
    // patient_admission_id is TEXT (string), fallback to visit_id if null/empty
    const admissionId = row.patient_admission_id && row.patient_admission_id.trim() !== '' 
      ? row.patient_admission_id 
      : row.visit_id;
    return `/viewinpatientbill/${row.visit_id}/${admissionId}/1`;
  }
  // Default fallback
  return `/billingreceipt/${row.visit_id}`;
};

const LinkButton = ({ to, children, className = "" }) => (
  <Link to={to}>
    <button className={`btn btn-link text-primary p-0 small fw-bold ${className}`} style={{ textDecoration: 'underline' }}>
      {children}
    </button>
  </Link>
);

const BackButton = ({ to, text }) => (
  <Link to={to}>
    <button className="btn btn-sm sms-btn small-btn px-3 py-2">
      <ArrowBackIcon fontSize="small" className="me-2" />
      Back to {text}
    </button>
  </Link>
);

const MedicineProfitReport = ({ actions, snackbars }) => {
  const [dateRange, setDateRange] = useState({
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10)
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ data: [], summary: {}, period: {} });

  const { start_date, end_date } = dateRange;
  const { data: rows = [], summary = {}, period = {} } = data;

  // DEBOUNCED SEARCH + FETCH
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReport();
    }, 400);

    return () => clearTimeout(timer);
  }, [start_date, end_date, searchTerm]);

  const fetchReport = async () => {
    if (!start_date || !end_date) return;
    setLoading(true);
    try {
      const res = await axios.get(`${config.smsUrl}/creport/sales_profit_loss`, {
        params: { 
          start_date, 
          end_date,
          search: searchTerm.trim() || undefined
        }
      });
      setData(res.data || { data: [], summary: {}, period: {} });
      actions.snackbarActions.successSnackbar?.("Report loaded successfully!");
    } catch (err) {
      actions.snackbarActions.errorSnackbar?.("Failed to load report");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const exportExcel = () => {
    const url = `${config.smsUrl}/creport/sales_profit_loss_export?format=excel&start_date=${start_date}&end_date=${end_date}`;
    window.open(searchTerm ? `${url}&search=${encodeURIComponent(searchTerm)}` : url);
  };

  const exportPdf = () => {
    const url = `${config.smsUrl}/creport/sales_profit_loss_export?format=pdf&start_date=${start_date}&end_date=${end_date}`;
    window.open(searchTerm ? `${url}&search=${encodeURIComponent(searchTerm)}` : url);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <>
      <PageTitle title="Medicine Sales & Profit Report" />
      <SubHeader title="Reports" subTitle="Medicine Profit & Loss">
        <FontAwesomeIcon icon={faPills} />
      </SubHeader>

      <Container className="mt-1">
        <MainSnackbar 
          open={snackbars.openBar} 
          variant={snackbars.type} 
          message={snackbars.message}
          handleCloseBar={actions.snackbarActions.hideSnackbar} 
        />

        {/* FILTER BAR */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body py-2 px-4">
            <div className="row g-2 align-items-center">
              <div className="col-auto">
                <BackButton to="/reports/generalreports" text="Reports" />
              </div>
              <div className="col-auto">
                <input type="date" name="start_date" value={start_date} onChange={handleDateChange} className="form-control form-control-sm" />
              </div>
              <div className="col-auto">
                <input type="date" name="end_date" value={end_date} onChange={handleDateChange} className="form-control form-control-sm" />
              </div>
              <div className="">
                <button onClick={fetchReport} disabled={loading} className="btn btn-primary btn-sm px-3">
                  {loading ? "..." : "Refresh"}
                </button>
                <button onClick={exportExcel} className="btn btn-success btn-sm px-3">
                  <FontAwesomeIcon icon={faFileExcel} className="me-1" /> Excel
                </button>
                <button onClick={exportPdf} className="btn btn-danger btn-sm px-3">
                  <FontAwesomeIcon icon={faFilePdf} className="me-1" /> PDF
                </button>
              </div>
              <div className="col">
                <input
                  type="text"
                  placeholder="Search patient, product, batch, visit..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="form-control form-control-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #28a745, #20c997)" }}>
              <div className="card-body text-center py-4">
                <h6 className="mb-2 opacity-75">Total Sales</h6>
                <h3 className="mb-0 fw-bold">${Number(summary.total_sales_usd || 0).toLocaleString()}</h3>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #dc3545, #fd7e14)" }}>
              <div className="card-body text-center py-4">
                <h6 className="mb-2 opacity-75">Total Cost</h6>
                <h3 className="mb-0 fw-bold">${Number(summary.total_cost_usd || 0).toLocaleString()}</h3>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #007bff, #6610f2)" }}>
              <div className="card-body text-center py-4">
                <h6 className="mb-2 opacity-75">NET PROFIT</h6>
                <h3 className="mb-0 fw-bold">${Number(summary.net_profit_usd || 0).toLocaleString()}</h3>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm bg-warning text-dark">
              <div className="card-body text-center py-4">
                <h6 className="mb-2">Profit Margin</h6>
                <h3 className="mb-0 fw-bold">{summary.profit_margin_percent || 0}%</h3>
              </div>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="card border-0 shadow">
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5"><LoadingGif /></div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="text-muted small text-uppercase">Date</th>
                      <th className="text-muted small text-uppercase">Visit</th>
                      <th className="text-muted small text-uppercase">Patient</th>
                      <th className="text-muted small text-uppercase">Product</th>
                      <th className="text-muted small text-uppercase text-center">Qty</th>
                      <th className="text-muted small text-uppercase text-end">Sale</th>
                      <th className="text-muted small text-uppercase">Curr</th>
                      <th className="text-muted small text-uppercase text-end">Cost</th>
                      <th className="text-muted small text-uppercase text-end">Profit</th>
                      <th className="text-muted small text-uppercase">Pay</th>
                      <th className="text-muted small text-uppercase">Batch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="text-center py-5 text-muted">
                          {searchTerm ? `No results found for "${searchTerm}"` : "No sales found for selected period"}
                        </td>
                      </tr>
                    ) : rows.map((row, i) => (
                      <tr key={i} className="align-middle">
                        <td className="small">{formatDate(row.sale_date)}</td>
                        <td>
                          <LinkButton to={getVisitLink(row)}>
                            <FontAwesomeIcon icon={faEye} className="me-1" />
                            {row.visit_id}
                          </LinkButton>
                        </td>
                        <td className="text-truncate" style={{ maxWidth: "130px" }} title={row.patient_name}>
                          {row.patient_name || '-'}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: "160px" }} title={row.product_name}>
                          {row.product_name || '-'}
                        </td>
                        <td className="text-center">{row.quantity || 0}</td>
                        <td className="text-end fw-bold text-success">
                          ${Number(row.sale_usd || 0).toFixed(2)}
                        </td>
                        <td>
                          <span className={`badge ${row.currency_sold === 'USD' ? 'bg-success' : 'bg-secondary'} badge-sm`}>
                            {row.currency_sold || 'SSP'}
                          </span>
                        </td>
                        <td className="text-end text-muted">
                          ${Number(row.total_cost_usd || 0).toFixed(2)}
                        </td>
                        <td className={`text-end fw-bold ${row.profit_usd >= 0 ? 'text-success' : 'text-danger'}`}>
                          ${Number(row.profit_usd || 0).toFixed(2)}
                        </td>
                        <td>
                          <span className={`badge ${row.payment_type === 'CASH' ? 'bg-success' : 'bg-info'} badge-sm`}>
                            {row.payment_type || '-'}
                          </span>
                        </td>
                        <td className="text-muted small">{row.batch_id || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="card-footer bg-white border-top-0 py-3 small text-muted text-center">
            <strong>{rows.length}</strong> records shown
            {searchTerm && ` • Searching: "${searchTerm}"`}
            {" "}• {period.start_date} → {period.end_date}
          </div>
        </div>
      </Container>
    </>
  );
};

const mapStateToProps = state => ({ snackbars: state.snackbar });
const mapDispatchToProps = dispatch => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) }
});

export default connect(mapStateToProps, mapDispatchToProps)(MedicineProfitReport);